import { useRef } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  ClickAwayListener,
  Divider,
  Fade,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Popper,
  TextField,
  Typography,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { StaticDateRangePicker } from "@mui/x-date-pickers-pro/StaticDateRangePicker";
import { RangeCalendarHeader } from "../../helpers/RangeCalendarHeader";
import {
  type CommittedSelection,
  type CompareTimeframeValue,
  getPrimaryLabel,
  PRIMARY_LIST_OPTIONS,
  useTimeFrameCompareState,
} from "../../helpers/timeFrameCompareState";

// Compare-to only ever offers these two — comparing against "Yesterday" is
// never an option here, regardless of which primary is currently selected.
const COMPARE_CANDIDATES: CompareTimeframeValue[] = [
  "two_days_ago",
  "three_days_ago",
];

// Primary list items get a divider after these values, grouping the
// relative-day options / MTD / Custom range visually.
const DIVIDER_AFTER = new Set(["three_days_ago", "mtd"]);

export type {
  CommittedSelection,
  PrimaryTimeframeValue,
  CompareTimeframeValue,
} from "../../helpers/timeFrameCompareState";
export { getPrimaryLabel, getCompareLabel } from "../../helpers/timeFrameCompareState";

// Two-pane widths for the custom-range layout, matching the Figma balance
// (left ~43%, calendar ~57%). 320 is the intrinsic width of a single MUI
// month calendar — going narrower makes it overflow rather than shrink.
const LIST_PANEL_WIDTH = 370; // left: predefined options
const CALENDAR_PANEL_WIDTH = 320; // right: single-month calendar
// +32 accounts for the container's p:2 (16px each side).
const CUSTOM_POPOVER_WIDTH = LIST_PANEL_WIDTH + CALENDAR_PANEL_WIDTH + 32;
// Wide enough for checkbox + "Compare to" label + comparison Autocomplete
// (minWidth 180) on one line without the row spilling past the popover.
const DEFAULT_POPOVER_WIDTH = 360;

interface Props {
  value: CommittedSelection;
  onApply: (next: CommittedSelection) => void;
  hasData: boolean;
  disabled?: boolean;
  dataTracker?: string;
}

const TimeFrameCompareControl = ({
  value,
  onApply,
  hasData,
  disabled,
  dataTracker,
}: Props) => {
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const {
    open,
    staged,
    isCustomPending,
    isComparePending,
    isApplyEnabled,
    isCompareDisabled,
    compareOptions,
    handleOpen: handleOpenState,
    handleDiscard,
    handleApply,
    handleCancelCustom,
    handleSelectPrimary,
    handleCompareToggle,
    setCustomRange,
    setCompareValue,
  } = useTimeFrameCompareState({
    value,
    onApply,
    hasData,
    compareCandidates: COMPARE_CANDIDATES,
    excludeCurrentPrimaryFromCompare: false,
  });

  const handleOpen = () => {
    if (disabled) return;
    handleOpenState();
  };

  // The comparison-date Autocomplete opens its option list in a Popper
  // portaled to document.body — outside this popover's own Popper — so
  // picking an option would otherwise register as a click-away and discard
  // the edit. Ignore clicks landing inside that portaled listbox.
  const handleClickAway = (event: MouseEvent | TouchEvent) => {
    const target = event.target as HTMLElement | null;
    if (target?.closest(".MuiAutocomplete-popper")) return;
    handleDiscard();
  };

  const triggerLabel = getPrimaryLabel(
    value.primary,
    value.customStart,
    value.customEnd,
  );

  const compareControls = (
    <Box
      display="flex"
      flexDirection={isCustomPending ? "column" : "row"}
      alignItems={isCustomPending ? "stretch" : "center"}
      gap={1}
    >
      <Box display="flex" alignItems="center" gap={1}>
        <Checkbox
          color="secondary"
          size="small"
          checked={staged.compareEnabled}
          disabled={isCompareDisabled || disabled}
          onChange={(e) => handleCompareToggle(e.target.checked)}
        />
        <Typography sx={{ fontSize: 14, whiteSpace: "nowrap" }}>
          Compare to
        </Typography>
      </Box>
      <Autocomplete
        size="small"
        disabled={isCompareDisabled || !staged.compareEnabled || disabled}
        options={compareOptions}
        value={compareOptions.find((o) => o.value === staged.compare) ?? null}
        isOptionEqualToValue={(o, v) => o.value === v.value}
        onChange={(_, newValue) => setCompareValue(newValue?.value ?? null)}
        sx={isCustomPending ? { width: "100%" } : { flex: 1, minWidth: 180 }}
        renderInput={(params) => (
          <TextField
            {...params}
            color="secondary"
            placeholder="Comparison date"
          />
        )}
      />
    </Box>
  );

  return (
    <>
      <Box ref={anchorRef}>
        <TextField
          color="secondary"
          required
          fullWidth
          size="small"
          label="Time Frame"
          value={triggerLabel}
          onClick={handleOpen}
          disabled={disabled}
          data-tracker={dataTracker}
          inputProps={{
            readOnly: true,
            style: { cursor: disabled ? "default" : "pointer" },
          }}
          sx={{
            "& .MuiInputBase-input": {
              cursor: disabled ? "default" : "pointer",
            },
          }}
          InputProps={{
            endAdornment: (
              <ArrowDropDownIcon sx={{ color: "rgba(0,0,0,0.54)" }} />
            ),
          }}
        />
      </Box>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-end"
        transition
        sx={{ zIndex: 1300 }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={200}>
            <Paper elevation={4} sx={{ mt: 1 }}>
              <ClickAwayListener onClickAway={handleClickAway}>
                <Box
                  sx={{
                    p: 2,
                    width: isCustomPending
                      ? CUSTOM_POPOVER_WIDTH
                      : DEFAULT_POPOVER_WIDTH,
                  }}
                >
                  <Typography sx={{ fontWeight: 500, mb: 1, fontSize: 14 }}>
                    Select Date or Choose Range
                  </Typography>
                  <Box display="flex">
                    <Box
                      sx={{
                        width: isCustomPending ? LIST_PANEL_WIDTH : "100%",
                        flexShrink: 0,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <List sx={{ py: 0 }}>
                        {PRIMARY_LIST_OPTIONS.map((opt) => {
                          const selected = staged.primary === opt.value;
                          return (
                            <Box key={opt.value}>
                              <ListItemButton
                                selected={selected}
                                disabled={disabled}
                                onClick={() => handleSelectPrimary(opt.value)}
                                sx={{
                                  borderRadius: 1,
                                  mb: 0.5,
                                  "&.Mui-selected": {
                                    backgroundColor: "#F7EEF9",
                                    color: "#9C27B0",
                                    "&:hover": { backgroundColor: "#F7EEF9" },
                                  },
                                }}
                              >
                                {opt.value === "custom" && (
                                  <CalendarMonthIcon
                                    fontSize="small"
                                    sx={{ mr: 1, color: "inherit" }}
                                  />
                                )}
                                <ListItemText
                                  primary={opt.listLabel}
                                  slotProps={{
                                    primary: { fontSize: 14 },
                                  }}
                                />
                              </ListItemButton>
                              {DIVIDER_AFTER.has(opt.value) && (
                                <Divider sx={{ my: 0.5 }} />
                              )}
                            </Box>
                          );
                        })}
                      </List>
                      {isCustomPending && (
                        <>
                          <Divider sx={{ my: 1 }} />
                          {compareControls}
                        </>
                      )}
                    </Box>
                    {isCustomPending && (
                      <Box
                        sx={{
                          pl: 1,
                          width: CALENDAR_PANEL_WIDTH,
                          flexShrink: 0,
                          minWidth: 0,
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <StaticDateRangePicker
                            calendars={1}
                            value={[staged.customStart, staged.customEnd]}
                            onChange={(newValue) =>
                              setCustomRange(newValue[0], newValue[1])
                            }
                            slots={{ calendarHeader: RangeCalendarHeader }}
                            slotProps={{
                              actionBar: { actions: [] },
                              toolbar: { hidden: true },
                            }}
                          />
                        </LocalizationProvider>
                        <Box
                          display="flex"
                          justifyContent="flex-end"
                          gap={1}
                          mt={1}
                        >
                          <Button
                            size="small"
                            color="secondary"
                            onClick={handleCancelCustom}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            color="secondary"
                            disabled={!isApplyEnabled || disabled}
                            onClick={handleApply}
                          >
                            Apply
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </Box>

                  {!isCustomPending && (
                    <>
                      <Divider sx={{ my: 1.5 }} />
                      {compareControls}
                      {isComparePending && (
                        <Box display="flex" justifyContent="flex-end" mt={1.5}>
                          <Button
                            size="small"
                            variant="contained"
                            color="secondary"
                            disabled={!isApplyEnabled || disabled}
                            onClick={handleApply}
                            fullWidth
                          >
                            Apply
                          </Button>
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              </ClickAwayListener>
            </Paper>
          </Fade>
        )}
      </Popper>
    </>
  );
};

export default TimeFrameCompareControl;
